// ─────────────────────────────────────────────────────────────────────────────
// ReConsentGate
//
// Shown when an existing user's last-accepted Terms or Privacy version is
// older than the current PRIVACY_VERSION / TERMS_VERSION constants in
// LegalDocument.js. Blocks the rest of the app until the user accepts the
// new versions (or signs out).
//
// On Accept:
//   - Updates profiles.agreed_to_terms_version + agreed_to_terms_at
//   - Updates profiles.agreed_to_privacy_version + agreed_to_privacy_at
//   - Calls refreshProfile() so the AppInner re-render un-gates the app
//
// Sign Out is the only escape — users can leave without agreeing, but they
// can't use the app without current consent on file.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from "react";
import { SUPABASE_URL, authFetch } from "../../lib/supabase";
import { C } from "../../lib/colors";
import { Btn } from "../../components/ui/Btn";
import { PRIVACY_VERSION, TERMS_VERSION } from "../legal/LegalDocument";

// Helper used by App.js to decide whether to render this gate.
// Returns true when EITHER stored version is older than the current
// constant, OR when either field is missing (legacy accounts).
export function isLegalConsentOutOfDate(profile) {
  if (!profile) return false;
  const storedTerms   = profile.agreed_to_terms_version   || "";
  const storedPrivacy = profile.agreed_to_privacy_version || "";
  return storedTerms !== TERMS_VERSION || storedPrivacy !== PRIVACY_VERSION;
}

export function ReConsentGate({ profile, userId, onAccepted, onSignOut }) {
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleAccept = async () => {
    if (!agreed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      const res = await authFetch(
        `${SUPABASE_URL}/rest/v1/profiles?id=eq.${userId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Prefer": "return=minimal",
          },
          body: JSON.stringify({
            agreed_to_terms_version:   TERMS_VERSION,
            agreed_to_terms_at:        now,
            agreed_to_privacy_version: PRIVACY_VERSION,
            agreed_to_privacy_at:      now,
          }),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || data.error || `Update failed (${res.status})`);
      }
      // Refresh profile so AppInner sees the new versions and un-gates.
      // If refreshProfile silently fails to flip our state, the user
      // would otherwise be stuck on "Saving…" forever — the finally
      // block below resets submitting so the user can retry or sign out
      // instead of being held hostage by a stuck UI state.
      await onAccepted?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Format the version (YYYY-MM-DD) as "May 23, 2026" for display
  const formatVersion = (iso) => {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-").map(Number);
    const monthName = ["January","February","March","April","May","June",
                       "July","August","September","October","November","December"][m - 1];
    return `${monthName} ${d}, ${y}`;
  };

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "#0a0c18",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16,
      zIndex: 9999,
      overflowY: "auto",
    }}>
      <div style={{
        maxWidth: 520,
        width: "100%",
        background: "#0f1322",
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 32,
        margin: "auto",
      }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📜</div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: "0 0 8px" }}>
            We've updated our terms
          </h1>
          <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.55, margin: 0 }}>
            Please review the changes and confirm to continue using Futures OS.
          </p>
        </div>

        {/* Version stamp */}
        <div style={{
          fontSize: 11,
          color: C.muted,
          textAlign: "center",
          marginBottom: 18,
          fontFamily: "monospace",
        }}>
          New version: <strong style={{ color: C.text }}>
            {formatVersion(
              PRIVACY_VERSION > TERMS_VERSION ? PRIVACY_VERSION : TERMS_VERSION
            )}
          </strong>
        </div>

        {/* What changed */}
        <div style={{
          background: C.blue + "10",
          border: `1px solid ${C.blue}30`,
          borderRadius: 8,
          padding: "14px 16px",
          marginBottom: 20,
        }}>
          <div style={{
            fontSize: 11,
            fontWeight: 700,
            color: C.blue,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            marginBottom: 10,
          }}>
            What changed
          </div>
          <ul style={{
            fontSize: 13,
            color: C.sub,
            lineHeight: 1.6,
            paddingLeft: 18,
            margin: 0,
          }}>
            <li style={{ marginBottom: 6 }}>
              <strong style={{ color: C.text }}>Data retention:</strong> the Privacy
              Policy now specifies how long we keep your data after cancellation —
              up to 180 days, after which it's permanently deleted. Details in
              Section 4 of the Privacy Policy.
            </li>
            <li style={{ marginBottom: 6 }}>
              <strong style={{ color: C.text }}>Refund policy:</strong> updated to a
              uniform no-refund policy (the previous 14-day annual refund clause
              has been removed).
            </li>
            <li style={{ marginBottom: 6 }}>
              <strong style={{ color: C.text }}>Section 17 (Termination):</strong> now
              describes the actual in-app account deletion flow, including the
              30-day grace period before permanent data purge.
            </li>
            <li>
              <strong style={{ color: C.text }}>General refresh:</strong> language
              clarifications and consistency improvements across both documents.
            </li>
          </ul>
        </div>

        {/* Doc links */}
        <div style={{
          display: "flex",
          gap: 10,
          marginBottom: 20,
          flexWrap: "wrap",
          justifyContent: "center",
        }}>
          <a
            href="/legal/terms"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: "8px 14px",
              fontSize: 12,
              fontWeight: 600,
              color: C.blue,
              background: "transparent",
              border: `1px solid ${C.blue}50`,
              borderRadius: 6,
              textDecoration: "none",
            }}
          >
            Read full Terms ↗
          </a>
          <a
            href="/legal/privacy"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: "8px 14px",
              fontSize: 12,
              fontWeight: 600,
              color: C.blue,
              background: "transparent",
              border: `1px solid ${C.blue}50`,
              borderRadius: 6,
              textDecoration: "none",
            }}
          >
            Read full Privacy Policy ↗
          </a>
        </div>

        {/* Acceptance checkbox */}
        <label style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          padding: "12px 14px",
          background: agreed ? C.green + "10" : "transparent",
          border: `1px solid ${agreed ? C.green + "40" : C.border}`,
          borderRadius: 8,
          cursor: "pointer",
          marginBottom: 16,
          transition: "all 0.15s",
        }}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={submitting}
            style={{
              marginTop: 2,
              width: 16,
              height: 16,
              accentColor: C.green,
              cursor: "pointer",
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 13, color: C.text, lineHeight: 1.55 }}>
            I have read and agree to the updated{" "}
            <a
              href="/legal/terms"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: C.blue, textDecoration: "underline" }}
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: C.blue, textDecoration: "underline" }}
            >
              Privacy Policy
            </a>.
          </span>
        </label>

        {error && (
          <div style={{
            padding: "10px 14px",
            background: C.red + "15",
            border: `1px solid ${C.red}40`,
            borderRadius: 6,
            fontSize: 12,
            color: C.red,
            marginBottom: 16,
          }}>
            ⚠ {error}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            onClick={handleAccept}
            disabled={!agreed || submitting}
            style={{
              padding: "12px 20px",
              background: agreed && !submitting ? C.green : C.border,
              color: "#fff",
              fontSize: 14,
              fontWeight: 700,
              border: "none",
              borderRadius: 6,
              cursor: agreed && !submitting ? "pointer" : "default",
              opacity: submitting ? 0.6 : 1,
              transition: "all 0.15s",
            }}
          >
            {submitting ? "Saving…" : "Accept and Continue"}
          </button>

          <Btn variant="ghost" onClick={onSignOut} disabled={submitting}>
            Sign out
          </Btn>
        </div>

        <p style={{
          fontSize: 11,
          color: C.muted,
          marginTop: 18,
          textAlign: "center",
          lineHeight: 1.55,
        }}>
          You must accept the updated terms to continue using Futures OS.
          Your acceptance is recorded with a timestamp and the document version.
        </p>
      </div>
    </div>
  );
}
